/**
 * SelectionTable Component
 *
 * TanStack Table with virtualization support for selecting items.
 * Used in wizard modals for vulnerability/asset selection.
 */

import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { InfiniteScrollIndicator } from '@/components/table/InfiniteScrollIndicator';
import { Spinner } from '@/components/ui/spinner';
import type { SearchableListItem } from '@/sections/attacks/attackPaths/components/wizards/hooks';
import { cn } from '@/utils/classname';

type SingleSelectProps = {
  mode?: 'single';
  selectedKey: string | null;
  onSelect: (item: SearchableListItem) => void;
  /**
   * Whether to visually highlight the selected row.
   * When false, selectedKey is still used for auto-scroll and semantic state,
   * but the row does not get visual highlight styling.
   * Useful in edit mode where selection is shown via "Currently Editing" display.
   * @default true
   */
  highlightSelected?: boolean;
};

type MultiSelectProps = {
  mode: 'multi';
  selectedKeys: Set<string>;
  onToggle: (item: SearchableListItem) => void;
  /** Header checkbox "select all visible" handler */
  onSelectAllVisible?: () => void;
  /** Header checkbox "clear all" handler */
  onClearAll?: () => void;
};

type SharedProps = {
  /** Data to display */
  data: SearchableListItem[];
  /** Column definitions */
  columns: ColumnDef<SearchableListItem>[];
  /** Loading state */
  isLoading?: boolean;
  /** Whether more pages are available */
  hasNextPage?: boolean;
  /** Whether currently fetching next page */
  isFetchingNextPage?: boolean;
  /** Callback to load more data */
  onLoadMore?: () => void;
  /** Default sort state */
  defaultSort?: SortingState;
  /** Threshold for enabling virtualization (default 50) */
  virtualizationThreshold?: number;
  /** Maximum visible rows (default 8, which gives ~400px height) */
  maxRows?: number;
  /** Explicit container height override in pixels */
  containerHeight?: number;
  /**
   * When true, the table fills its flex parent's height (`flex-1 min-h-0`)
   * instead of using a fixed `containerHeight`/`maxRows` pixel height. The
   * parent must be a fixed-height flex column. In this mode the
   * `InfiniteScrollIndicator` footer shrinks the scroll area rather than
   * growing the overall layout, so the surrounding dialog/card never changes
   * height when rows are present. Opt-in and additive: when falsy, the
   * `containerHeight ?? maxRows*ROW_HEIGHT + TABLE_HEADER_HEIGHT` pixel-height
   * path is used unchanged.
   * @default false
   */
  fillHeight?: boolean;
};

/**
 * Props for SelectionTable component (discriminated union for single vs multi select)
 */
export type SelectionTableProps = (SingleSelectProps | MultiSelectProps) &
  SharedProps;

/**
 * Row height in pixels.
 *
 * Rows render a two-line NameCell (name `text-sm`/20px + 2px gap + metadata
 * `text-xs`/16px) inside `py-1` (8px) cells ≈ 46px of content. This must be
 * >= that content height so a row never grows past it: the container height is
 * computed as `maxRows * ROW_HEIGHT + header`, and rows are pinned to exactly
 * ROW_HEIGHT in BOTH virtualized and non-virtualized modes, so the viewport
 * always shows a whole number of rows with no partially-clipped final row.
 */
export const ROW_HEIGHT = 48;

/**
 * Table header height in pixels.
 * The header is inside the scroll container, so we must account for it.
 * Composed of: py-2 (16px) + text-xs (~12px) + line-height (~4px) = ~32px
 */
export const TABLE_HEADER_HEIGHT = 32;

/**
 * Scroll threshold for loading more (pixels from bottom)
 */
const SCROLL_THRESHOLD = 100;

/**
 * SelectionTable displays a sortable, selectable table with optional virtualization.
 *
 * Features:
 * - TanStack Table for sorting and column management
 * - Virtual scrolling for large datasets
 * - Row selection with highlight
 * - Infinite scroll support
 *
 * @example
 * ```tsx
 * <SelectionTable
 *   data={items}
 *   columns={vulnerabilityColumns}
 *   selectedKey={selectedId}
 *   onSelect={handleSelect}
 * />
 * ```
 */
export function SelectionTable(props: SelectionTableProps) {
  const {
    data,
    columns,
    isLoading = false,
    hasNextPage = false,
    isFetchingNextPage = false,
    onLoadMore,
    defaultSort = [],
    virtualizationThreshold = 50,
    maxRows = 8,
    containerHeight,
    fillHeight = false,
  } = props;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [sorting, setSorting] = useState<SortingState>(defaultSort);

  // Determine if pagination is possible (props were provided)
  const hasPaginationSupport = onLoadMore !== undefined;

  // Calculate container height from maxRows or explicit override
  // Include TABLE_HEADER_HEIGHT because the header is inside the scroll container
  const calculatedHeight =
    containerHeight ?? maxRows * ROW_HEIGHT + TABLE_HEADER_HEIGHT;

  // Initialize TanStack Table
  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: row => row.key,
  });

  const { rows } = table.getRowModel();

  // Determine if virtualization should be enabled
  const useVirtualization = data.length > virtualizationThreshold;

  // Virtual row management
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 5,
    enabled: useVirtualization,
  });

  // Derive the single-mode selected key for auto-scroll purposes
  const singleSelectedKey =
    props.mode !== 'multi' ? (props.selectedKey ?? null) : null;

  // Auto-scroll to selected item when data loads or selection changes (single mode / edit mode)
  useEffect(() => {
    // Skip if no selection or no data
    if (!singleSelectedKey || rows.length === 0) return;

    // Find the index of the selected row in the sorted/filtered table data
    const selectedIndex = rows.findIndex(row => row.id === singleSelectedKey);

    // If item not found (filtered out or custom), don't scroll
    if (selectedIndex === -1) return;

    // Use requestAnimationFrame to ensure layout is complete before scrolling
    const scrollFrame = requestAnimationFrame(() => {
      if (useVirtualization && virtualizer) {
        // Virtualized mode: use virtualizer's scrollToIndex for efficiency
        virtualizer.scrollToIndex(selectedIndex, { align: 'center' });
      } else {
        // Non-virtualized mode: use native DOM scrollIntoView
        const row = scrollContainerRef.current?.querySelector(
          `[data-testid="selection-table-row-${singleSelectedKey}"]`
        );
        if (row) {
          row.scrollIntoView({ block: 'center', behavior: 'instant' });
        }
      }
    });

    // Cleanup: cancel animation frame if effect re-runs or unmounts
    return () => cancelAnimationFrame(scrollFrame);
  }, [singleSelectedKey, rows, useVirtualization, virtualizer]);

  const virtualRows = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  // Handle scroll for infinite loading
  const handleScroll = useCallback(() => {
    if (!onLoadMore || !hasNextPage || isFetchingNextPage) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    if (distanceFromBottom < SCROLL_THRESHOLD) {
      onLoadMore();
    }
  }, [onLoadMore, hasNextPage, isFetchingNextPage]);

  // Loading state
  if (isLoading) {
    return (
      <div
        className={cn(
          'flex items-center justify-center',
          fillHeight && 'h-full min-h-0 flex-1'
        )}
        style={fillHeight ? undefined : { height: calculatedHeight }}
        data-testid="selection-table-loading"
      >
        <Spinner size="lg" />
      </div>
    );
  }

  // Empty state
  if (data.length === 0) {
    return (
      <div
        className={cn(
          'text-chariot-text-secondary flex items-center justify-center',
          fillHeight && 'h-full min-h-0 flex-1'
        )}
        style={fillHeight ? undefined : { height: calculatedHeight }}
      >
        No results found
      </div>
    );
  }

  const isMulti = props.mode === 'multi';

  // Multi-mode helpers (derived once here, used in renderRow and header)
  const multiSelectedKeys = isMulti ? props.selectedKeys : null;
  const allVisibleIds = rows.map(r => r.id);
  const allVisibleSelected =
    isMulti &&
    allVisibleIds.length > 0 &&
    allVisibleIds.every(id => multiSelectedKeys!.has(id));
  const someVisibleSelected =
    isMulti && allVisibleIds.some(id => multiSelectedKeys!.has(id));
  const headerCheckboxIndeterminate =
    isMulti && someVisibleSelected && !allVisibleSelected;

  /**
   * Render a data row with proper styling.
   * @param row - The table row data
   * @param style - Optional inline styles (used for virtualization height)
   * @param showTopBorder - Whether to show top border (false for first visible row)
   */
  const renderRow = (
    row: (typeof rows)[0],
    style?: CSSProperties,
    showTopBorder = true
  ) => {
    if (isMulti) {
      const isSelected = multiSelectedKeys!.has(row.id);
      return (
        <tr
          key={row.id}
          data-testid={`selection-table-row-${row.id}`}
          onClick={() => props.onToggle(row.original)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              props.onToggle(row.original);
            }
          }}
          style={style}
          tabIndex={0}
          role="option"
          aria-selected={isSelected}
          className={cn(
            'group cursor-pointer transition-colors duration-150',
            'hover:bg-chariot-background-secondary',
            'focus:ring-chariot-brand-primary outline-hidden focus:ring-2 focus:ring-inset',
            showTopBorder && 'border-chariot-stroke border-t',
            isSelected && 'bg-chariot-background-secondary'
          )}
        >
          {/* Leading checkbox cell */}
          <td className="px-3 py-1" style={{ width: 40, minWidth: 40 }}>
            <input
              type="checkbox"
              checked={isSelected}
              aria-checked={isSelected}
              onChange={() => props.onToggle(row.original)}
              onClick={e => e.stopPropagation()}
              className="border-chariot-stroke accent-chariot-brand-primary size-4 cursor-pointer rounded"
            />
          </td>
          {row.getVisibleCells().map(cell => (
            <td key={cell.id} className="px-3 py-1">
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </td>
          ))}
        </tr>
      );
    }

    // Single mode
    const isMatchingSelection = row.id === singleSelectedKey;
    const highlightSelected =
      (props as SingleSelectProps).highlightSelected ?? true;
    const shouldHighlight = highlightSelected && isMatchingSelection;

    return (
      <tr
        key={row.id}
        data-testid={`selection-table-row-${row.id}`}
        onClick={() => (props as SingleSelectProps).onSelect(row.original)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            (props as SingleSelectProps).onSelect(row.original);
          }
        }}
        style={style}
        tabIndex={0}
        role="option"
        aria-selected={isMatchingSelection}
        className={cn(
          'group cursor-pointer transition-colors duration-150',
          'hover:bg-chariot-background-secondary',
          'focus:ring-chariot-brand-primary outline-hidden focus:ring-2 focus:ring-inset',
          // Border on top of row (except first visible row)
          showTopBorder && 'border-chariot-stroke border-t',
          shouldHighlight &&
            'is-selected bg-chariot-stroke text-chariot-text-primary'
        )}
      >
        {row.getVisibleCells().map(cell => (
          <td key={cell.id} className="px-3 py-1">
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        ))}
      </tr>
    );
  };

  return (
    <div className={cn('flex flex-col', fillHeight && 'h-full min-h-0')}>
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        data-testid="selection-table-scroll-container"
        className={cn(
          'border-chariot-stroke bg-chariot-background-primary overflow-auto rounded-lg border',
          fillHeight && 'min-h-0 flex-1'
        )}
        style={fillHeight ? undefined : { height: calculatedHeight }}
      >
        <table className="w-full table-fixed text-sm" role="table">
          {/* colgroup enforces column widths - columns without explicit size are flexible */}
          <colgroup>
            {/* Leading checkbox col in multi mode */}
            {isMulti && (
              <col style={{ width: 40, minWidth: 40, maxWidth: 40 }} />
            )}
            {table.getAllColumns().map(column => {
              const size = column.getSize();
              // Use explicit meta.flexible marker instead of relying on TanStack Table internals
              const isFlexible =
                (column.columnDef.meta as { flexible?: boolean })?.flexible ??
                false;

              return (
                <col
                  key={column.id}
                  style={
                    isFlexible
                      ? undefined // Flexible: takes remaining space
                      : {
                          width: size,
                          minWidth: size,
                          maxWidth: size,
                        }
                  }
                />
              );
            })}
          </colgroup>
          <thead className="bg-chariot-background-primary sticky top-0 z-10">
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {/* Header checkbox cell (multi mode only) */}
                {isMulti && (
                  <th
                    className="bg-chariot-background-primary px-3 py-2"
                    style={{ width: 40, minWidth: 40, maxWidth: 40 }}
                  >
                    {(props.onSelectAllVisible || props.onClearAll) && (
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        ref={el => {
                          if (el)
                            el.indeterminate = headerCheckboxIndeterminate;
                        }}
                        aria-checked={
                          headerCheckboxIndeterminate
                            ? 'mixed'
                            : allVisibleSelected
                        }
                        onChange={() => {
                          if (someVisibleSelected) {
                            props.onClearAll?.();
                          } else {
                            props.onSelectAllVisible?.();
                          }
                        }}
                        className="border-chariot-stroke accent-chariot-brand-primary size-4 cursor-pointer rounded"
                      />
                    )}
                  </th>
                )}
                {headerGroup.headers.map(header => {
                  const isSorted = header.column.getIsSorted();

                  return (
                    <th
                      key={header.id}
                      className={cn(
                        'bg-chariot-background-primary text-chariot-text-secondary px-3 py-2 text-left text-xs font-semibold uppercase',
                        header.column.getCanSort() &&
                          'cursor-pointer select-none'
                      )}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <div className="flex items-center gap-1">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {isSorted === 'asc' && (
                          <ChevronUpIcon className="size-3" />
                        )}
                        {isSorted === 'desc' && (
                          <ChevronDownIcon className="size-3" />
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {useVirtualization ? (
              // Virtualized rendering with spacer rows for scroll positioning
              <>
                {/* Top spacer - only render if there's space to fill */}
                {virtualRows.length > 0 && virtualRows[0].start > 0 && (
                  <tr
                    style={{ height: virtualRows[0].start }}
                    aria-hidden="true"
                  >
                    <td colSpan={columns.length + (isMulti ? 1 : 0)} />
                  </tr>
                )}
                {/* Visible rows */}
                {virtualRows.map((virtualRow, index) => {
                  const row = rows[virtualRow.index];
                  // First visible row has no top border if spacer exists, otherwise check if it's the first row overall
                  const isFirstVisible =
                    index === 0 && virtualRows[0].start === 0;
                  return renderRow(
                    row,
                    { height: ROW_HEIGHT },
                    !isFirstVisible
                  );
                })}
                {/* Bottom spacer - only render if there's space to fill */}
                {(() => {
                  const bottomHeight =
                    totalSize - (virtualRows[virtualRows.length - 1]?.end ?? 0);
                  return (
                    virtualRows.length > 0 &&
                    bottomHeight > 0 && (
                      <tr style={{ height: bottomHeight }} aria-hidden="true">
                        <td colSpan={columns.length + (isMulti ? 1 : 0)} />
                      </tr>
                    )
                  );
                })()}
              </>
            ) : (
              // Regular rendering for small datasets. Pin each row to
              // ROW_HEIGHT (matching virtualized mode) so the computed
              // container height shows whole rows with no partial final row.
              rows.map((row, index) =>
                renderRow(row, { height: ROW_HEIGHT }, index > 0)
              )
            )}
          </tbody>
        </table>
      </div>

      {/* Scroll indicator using shared component with relative variant */}
      {hasPaginationSupport && (
        <InfiniteScrollIndicator
          variant="relative"
          isLoading={isFetchingNextPage}
          hasMore={hasNextPage === true}
          itemCount={data.length}
          minItemsThreshold={10}
        />
      )}
    </div>
  );
}
